import { defineComponent } from 'vue';
import { mapWritableState } from 'pinia';
import { useLocationStore } from '@/store/LocationStore';
import { useRegisterTestStore } from '@/store/RegisterTestStore';
import { Validator } from '@/validator/Validator';
import FloatingCard from '@/components/Shared/RegisterTest/FloatingCard/FloatingCard.vue';
import ThemesHelper from '@/helpers/ThemesHelper';
import BaseInput from '@/components/BaseForm/BaseInput/BaseInput.vue';
import BaseSelect from '@/components/BaseForm/BaseSelect/BaseSelect.vue';
import AlertBox from '@/components/AlertBox/AlertBox.vue';
import axios from 'axios';

interface userLocationInterface {
  state: string;
  city: string;
}

export default defineComponent({
  props: {
    active: {
      type: Boolean,
    },
  },
  components: {
    FloatingCard,
    BaseInput,
    BaseSelect,
    AlertBox,
  },
  computed: {
    ...mapWritableState(useRegisterTestStore, ['canJumpNext']),
    ...mapWritableState(useRegisterTestStore, ['patient']),
    validRequiredFields() {
      if (
        this.patient.state ||
        this.patient.city ||
        this.patient.age ||
        this.patient.gender ||
        this.patient.education_level ||
        this.patient.sexual_orientation
      ) {
        return true;
      } else {
        return false;
      }
    },
    minorAge() {
      if (this.patient.age && this.patient.age < 12) {
        return true;
      }
      return false;
    },
  },
  watch: {
    patient: {
      handler: function () {
        setTimeout(() => {
          if (this.active) {
            this.canJumpNext = this.isValidForm(false);
          }
        }, 200);
      },
      deep: true,
    },
  },
  data() {
    return {
      StepContent: ThemesHelper.getThemeParams('testStepFourContents'),
      genderOptions: [
        { name: 'Mulher', value: 'male' },
        { name: 'Homem', value: 'female' },
        { name: 'Mulher transgênero', value: 'transgender_woman' },
        { name: 'Homem transgênero', value: 'transgender_man' },
        { name: 'Não binário', value: 'non_binary' },
        { name: 'Outro', value: 'uninformed' },
      ],
      states: [{ false: false }],
      locationStore: useLocationStore(),
      cities: [],
      orientationOptions: [
        { name: 'Heterossexual', value: 'straight' },
        { name: 'Homossexual', value: 'homosexual' },
        { name: 'Bissexual', value: 'bisexual' },
        { name: 'Assexual', value: 'asexual' },
        { name: 'Pansexual', value: 'pansexual' },
        { name: 'Outro', value: 'other' },
      ],
      scholarityOptions: [
        { name: 'Infantil', value: 'childhood_education' },
        {
          name: 'Fundamental incompleto',
          value: 'middle_school_incomplete',
        },
        { name: 'Fundamental completo', value: 'middle_school_complete' },
        { name: 'Médio incompleto', value: 'high_school_incomplete' },
        { name: 'Médio completo', value: 'high_school_complete' },
        { name: 'Superior incompleto', value: 'college_incomplete' },
        { name: 'Superior completo', value: 'college_complete' },
        { name: 'Não estudou', value: 'uneducated' },
      ],
      mustAccept: false,
      requiredField: [
        { validation: 'requiredField', message: 'Você deve selecionar uma opção.' },
      ],
      requiredAge: [
        { validation: 'requiredField', message: 'Insira sua idade.' },
        { validation: 'validAge', message: 'O campo aceita apenas idade válida.' },
      ],
      removeMessages: false,
      toggleActive: false,
      userLocation: {} as userLocationInterface,
    };
  },
  mounted() {
    this.getStates();
  },
  methods: {
    async getStates() {
      const response: any = await this.locationStore.states();
      this.states = response;
    },
    async getCities(stateSelected: any) {
      const response: any = await this.locationStore.cities(stateSelected.id);
      this.cities = response;
      if (this.toggleActive) this.patient.city = this.userLocation.city;
    },
    handlerStateChange(stateSelected: any) {
      if (
        !(this.toggleActive && this.userLocation.state == stateSelected.sigla)
      )
        this.toggleActive = false;
      this.patient.city = '';
      this.getCities(stateSelected);
    },
    async getStreetAddressFrom(lat: any, long: any) {
      try {
        const res = await axios.get(
          `https://revgeocode.search.hereapi.com/v1/revgeocode?at=${lat},${long}&lang=pt-BR/&apiKey=It9_J3dNi3lQhlQDIgZOHWXOQtWuvv_u6kmeS984FZI`
        );

        this.userLocation.state = res.data.items[0].address.stateCode;
        this.userLocation.city = res.data.items[0].address.city;
        this.patient.state = this.userLocation.state;
      } catch (error) {
        this.patient.state = '';
      }
    },
    async getUserLocation() {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.patient.state = '';
          this.getStreetAddressFrom(
            position.coords.latitude,
            position.coords.longitude
          );
        },
        (error) => {
          this.toggleActive = false;
          if (error.code == error.PERMISSION_DENIED) {
            alert('Permissão para acessar a geolocalização bloqueada ');
          }
        }
      );
    },
    handlerToggleLocation() {
      this.toggleActive = !this.toggleActive;
      if (this.toggleActive) {
        this.getUserLocation();
      } else {
        this.patient.state = '';
        this.patient.city = '';
      }
    },
    errorValidation() {
      this.isValidForm();
    },
    isValidForm(showErrors = true) {
      this.mustAccept = showErrors && !this.patient.accept_terms;
      const validator = Validator.validateForm(this.$refs, showErrors) && this.patient.accept_terms;
      this.removeMessages = (validator) ? true : false;
      return validator;
    },
    isValidStep() {
      return this.isValidForm(false);
    },
    setAnimationType(animationName: string) {
      setTimeout(() => {
        const baseModal: any = this.$refs['base-modal'];
        switch (animationName) {
          case 'setNextDismissAnimation':
            baseModal.setNextDismissAnimation();
            break;
          case 'setNextShowAnimation':
            baseModal.setNextShowAnimation();
            break;
          case 'setPreviousDismissAnimation':
            baseModal.setPreviousDismissAnimation();
            break;
          case 'setPreviousShowAnimation':
            baseModal.setPreviousShowAnimation();
            break;
        }
      }, 1);
    },
  },
});
